/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components';
import InterestCards from './InterestCards';
import OutcomeButtons from './OutcomeButtons';
import AISummaryPrompt from './AISummaryPrompt';

const INTEREST_OPTIONS = [
  {
    value: 'hot',
    label: 'HOT',
    emoji: '🔥',
    description: 'Highly interested, likely to book or proceed with treatment',
    variant: 'hot',
  },
  {
    value: 'warm',
    label: 'WARM',
    emoji: '☀️',
    description: 'Some interest, may need more persuasion',
    variant: 'warm',
  },
  {
    value: 'cold',
    label: 'COLD',
    emoji: '❄️',
    description: 'Not ready or not serious',
    variant: 'cold',
  },
];

/**
 * CloseCallLive - Component for closing the call
 * - Interest level selection (HOT, WARM, COLD)
 * - AI Summary generation prompt
 * - Final outcome buttons
 */
const DEFAULT_FOLLOWUP_TEXT = 'Ensure WARM and COLD leads are tagged for proactive follow-up within specific timeframes (24 hours, 1 week, etc.) with email/SMS nudges linking testimonials or offers. Enter on CRM.';

const CloseCallLive = ({
  formData,
  onChange,
  isAiEnabled,
  aiSummaryLoading,
  onGenerateAISummary,
  onOutcomeSelect,
  disabled,
  previewMode,
  metadata,
}) => {
  const theme = useTheme();

  // Get followUp text from metadata or use default
  const followUpText = metadata?.hints?.followUp || DEFAULT_FOLLOWUP_TEXT;

  // All outcome options - available for both INBOUND and OUTBOUND
  const outcomeOptions = [
    {
      value: 'submit',
      label: 'Submit',
      iconName: 'formkit:submit',
      variant: 'submit',
    },
    {
      value: 'not-booked',
      label: 'Appointment Not Booked',
      iconName: 'ant-design:calendar-filled',
      variant: 'not-booked',
    },
    {
      value: 'booked',
      label: 'Book An Appointment',
      iconName: 'akar-icons:circle-check-fill',
      variant: 'booked',
    },
  ];

  // Handle interest level change
  const handleInterestChange = useCallback((value) => {
    if (disabled || previewMode) return;
    onChange('interestLevel', value);
  }, [onChange, disabled, previewMode]);

  // Handle outcome selection
  const handleOutcomeChange = useCallback((value) => {
    if (disabled || previewMode) return;
    onChange('callOutcome', value);
    if (onOutcomeSelect) {
      onOutcomeSelect(value);
    }
  }, [onChange, onOutcomeSelect, disabled, previewMode]);

  // Section label style
  const sectionLabelSx = {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    mb: 0.5,
  };

  const sectionSubtitleSx = {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    mb: 2,
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Interest Level Section */}
      <Box>
        <Typography sx={sectionLabelSx}>Assessing Interest Level</Typography>
        <Typography sx={sectionSubtitleSx}>Internal tracking for team</Typography>

        {previewMode ? (
          <Box sx={{ opacity: 0.7, pointerEvents: 'none' }}>
            <InterestCards
              options={INTEREST_OPTIONS}
              selectedValue={null}
              onChange={() => {}}
            />
          </Box>
        ) : (
          <InterestCards
            options={INTEREST_OPTIONS}
            selectedValue={formData.interestLevel}
            onChange={handleInterestChange}
          />
        )}

        {/* Follow-up System Hint */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: '12px',
            bgcolor: 'rgba(255, 171, 0, 0.08)',
            border: '1px solid rgba(255, 171, 0, 0.24)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}
        >
          <Iconify
            icon="mdi:information-outline"
            width={20}
            height={20}
            sx={{ color: '#FFAB00', flexShrink: 0, mt: 0.25 }}
          />
          <Box>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#B76E00', mb: 0.25 }}>
              Follow-Up System:
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#B76E00', lineHeight: 1.5 }}>
              {followUpText}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Final Outcome Section */}
      <Box>
        <Typography sx={sectionLabelSx}>Final Outcome</Typography>
        <Typography sx={sectionSubtitleSx}>Select the call result</Typography>

        {previewMode ? (
          // Preview mode
          <Box>
            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: '10px',
                bgcolor: 'rgba(0, 184, 217, 0.08)',
                border: '1px solid rgba(0, 184, 217, 0.24)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Iconify icon="mdi:information" width={20} height={20} sx={{ color: '#00B8D9' }} />
              <Typography sx={{ fontSize: '0.875rem', color: '#006C9C' }}>
                This is a preview. Outcome selection is available when using the script with a lead.
              </Typography>
            </Box>
            <OutcomeButtons
              options={outcomeOptions}
              selectedValue={null}
              onChange={() => {}}
              disabled
            />
          </Box>
        ) : (
          // Live mode
          <>
            {/* AI Summary Prompt */}
            {isAiEnabled && (
              <>
                <AISummaryPrompt
                  onGenerateSummary={onGenerateAISummary}
                  isLoading={aiSummaryLoading}
                  disabled={disabled}
                />

                {/* Divider with "or" */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    my: 2,
                  }}
                >
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#9CA3AF',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    or continue without
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
                </Box>
              </>
            )}

            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#637381', mb: 1.5 }}>
              How did the call end?
            </Typography>
            <OutcomeButtons
              options={outcomeOptions}
              selectedValue={formData.callOutcome}
              onChange={handleOutcomeChange}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

CloseCallLive.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func,
  isAiEnabled: PropTypes.bool,
  aiSummaryLoading: PropTypes.bool,
  onGenerateAISummary: PropTypes.func,
  onOutcomeSelect: PropTypes.func,
  disabled: PropTypes.bool,
  previewMode: PropTypes.bool,
  metadata: PropTypes.shape({
    hints: PropTypes.shape({
      followUp: PropTypes.string,
    }),
  }),
};

CloseCallLive.defaultProps = {
  formData: {},
  onChange: () => {},
  isAiEnabled: false,
  aiSummaryLoading: false,
  onGenerateAISummary: () => {},
  onOutcomeSelect: null,
  disabled: false,
  previewMode: false,
  metadata: null,
};

export default CloseCallLive;
